import { useEffect, useState } from 'react';
import axiosInstance from 'services/axiosInstance';
import * as S from './styles';
//Components
import AmountItems from '../../components/DashboardSection/AmountItems';
import TotalItems from '../../components/DashboardSection/TotalItems';
import Auth401 from "../../components/Error/Auth";
import Erro429 from "../../components/Error/Erro429";
import Loading from '../../components/Loading';
import TitleDash from '../../components/Title/TitleDash';
//hooks
import useLoading from '../../hooks/useLoading';
import { useMultipleModal } from '../../hooks/useMultipleModal';
//interface
import { IDashboard, CashFlow } from 'Interfaces/IDashboard';
import { IOrder } from "Interfaces/IOrder";
//utils
import { CurrencyFormat } from 'utils/CurrencyFormat';
//icons
import {
    CircleHalfTilt,
    Eyeglasses,
    Gift,
    Screwdriver,
    ShoppingCart,
    UsersThree,
} from "@phosphor-icons/react";
// Gráficos - usando Recharts
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ComposedChart,
    Line,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

export default function Dashboard() {
    const { isLoading, startLoading, stopLoading } = useLoading();
    const { isOpenModal, toggleModalVisibility } = useMultipleModal();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [oldestYear, setOldestYear] = useState(new Date().getFullYear());
    const [dataApi, setDataApi] = useState<IDashboard | null>(null);
    const COLORS = ['#FF0000', '#E4E4E7'];
    const RADIAN = Math.PI / 180;
    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }: any) => {
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };
    const CustomDot = (props: any) => {
        const { cx, cy } = props;
        if (cx === undefined || cy === undefined) return null;
        return (
            <circle
                cx={cx}
                cy={cy}
                r={4}
                fill="#fff"
                stroke="#FF0000"
                strokeWidth={1}
                style={{
                    filter: 'drop-shadow(0 0 2px #ff00005e) drop-shadow(0 0 1px #3b82f6)',
                }}
            />
        );
    };

    const fetchData = async () => {
        startLoading();
        try {
            let url = `/dashboard`;
            if (currentDate) {
                url += `?year=${currentDate.getFullYear()}&month=${currentDate.getMonth() + 1}`;
            }
            const [response, responseSale] = await Promise.all([
                axiosInstance.get(url),
                axiosInstance.get(`sale`),
            ]);
            const sale = responseSale.data.data;
            const dashboard = response.data;

            setDataApi(dashboard);
            const salesYears = sale.map((sale: IOrder) => new Date(sale.date_sale).getFullYear());
            setOldestYear(salesYears.length > 0 ? Math.min(...salesYears) : new Date().getFullYear());
        } catch (error: any) {
            if (error.status === 401) {
                toggleModalVisibility('auth');
            } else if (error.status === 429) {
                toggleModalVisibility('request429');
            }
            console.log("Error: ", error);
        } finally {
            stopLoading();
        }
    };

    useEffect(() => {
        const debounceFetch = setTimeout(() => {
            fetchData();
        }, 300);

        return () => clearTimeout(debounceFetch);
    }, [currentDate]);

    if (isOpenModal("auth")) {
        return (
            <Auth401 isOpen={isOpenModal("auth")} />
        )
    } else if (isOpenModal("request429")) {
        return (
            <Erro429 />
        )
    }

    // Transformar os dados para o formato aceito pelo Recharts
    const transformMonthlySales = (
        monthlySales: Record<number, string>,
    ) => {
        // Array com nomes dos meses
        const monthNames = [
            "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
            "Jul", "Ago", "Set", "Out", "Nov", "Dez"
        ];

        // Converter o objeto em um array de objetos
        return Object.entries(monthlySales).map(([month, sales]) => {
            const monthIndex = parseInt(month) - 1; // Ajuste porque meses vêm de 1-12, mas índices são 0-11
            return {
                name: monthNames[monthIndex],
                vendas: parseFloat(sales),
                month: parseInt(month) // Armazenar o número do mês para uso na lógica de cores
            };
        });
    };

    const transformCashFlowToPieData = (cashFlow: CashFlow) => {
        return [
            { name: 'Entradas', value: parseFloat(cashFlow.gains) || 0 },
            { name: 'Saídas', value: parseFloat(cashFlow.expense_with_category) || 0 }
        ].filter(item => item.value > 0); // Opcional: filtrar valores zero
    };

    return (
        <S.Container>
            <TitleDash setDateTitlle={setCurrentDate} startingYear={oldestYear}/>

            {isLoading || !dataApi ? (
                <Loading />
            ) : (

                <S.Content>
                    <S.ContentItems isFill={false}>
                        <TotalItems name='Saldo mensal' value={dataApi.cash_flow.balance} />
                        <TotalItems name='Entrada mensal' value={dataApi.cash_flow.gains} />
                        <TotalItems name='Saída mensal' value={dataApi.cash_flow.expense} />
                        <TotalItems name='Retirada' value={dataApi.cash_flow.expense_with_category} />
                        <TotalItems name='Receber mensal' value={dataApi.cash_flow.gainsPending} />
                    </S.ContentItems>

                    <S.ContentItems isFill={true}>
                        <AmountItems
                            value={dataApi.amount_items.frames || 0}
                            Icons={<Eyeglasses size={24} />}
                            name='Armação'
                        />
                        <AmountItems
                            value={dataApi.amount_items.service || 0}
                            Icons={<Screwdriver size={24} />}
                            name='Serviço'
                        />
                        <AmountItems
                            value={dataApi.amount_items.accessories || 0}
                            Icons={<Gift size={24} />}
                            name='Acessório'
                        />
                        <AmountItems
                            value={dataApi.amount_items.lenses || 0}
                            Icons={<CircleHalfTilt size={24} />}
                            name='Lente'
                        />
                        <AmountItems
                            value={dataApi.amount_items.customers || 0}
                            Icons={<UsersThree size={24} />}
                            name='Cliente'
                        />
                        <AmountItems
                            value={dataApi.amount_items.sales || 0}
                            Icons={<ShoppingCart size={24} />}
                            name='Venda'
                        />
                    </S.ContentItems>

                    <S.DividerGraph>
                        <S.FrameGraph isLarge={false}>
                            <S.LabelGraph>
                                Total de venda por pagamento <span>(R$)</span>
                            </S.LabelGraph>
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart
                                    layout="vertical"
                                    width={500}
                                    height={400}
                                    data={dataApi.payment_methods}
                                    margin={{
                                        top: 20,
                                        right: 20,
                                        bottom: 20,
                                        left: 0,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                                    <XAxis type="number" />
                                    <YAxis
                                        dataKey="payment_method"
                                        type="category"
                                        scale="band"
                                        width={140}
                                        tick={{ fontSize: 14 }}
                                    />
                                    <Tooltip
                                        formatter={(value, name) => [CurrencyFormat(Number(value)), name]}
                                    />
                                    <Bar
                                        dataKey="value"
                                        name="Total em R$"
                                        barSize={20}
                                        label={{ position: 'top' }}
                                        radius={[0, 8, 8, 0]}
                                        fill='#FF0000'
                                    >
                                        {
                                            dataApi.payment_methods.map((entry: any, index: number) => {
                                                const maxValue = Math.max(...dataApi.payment_methods.map((item: any) => parseFloat(item.value)));
                                                return (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={parseFloat(entry.value) === maxValue ? '#FF0000' : '#D4D4D8'} />
                                                )
                                            }
                                            )}
                                    </Bar>
                                </ComposedChart>
                            </ResponsiveContainer>
                        </S.FrameGraph>

                        <S.FrameGraph isLarge={true}>
                            <S.LabelGraph>
                                Total de venda por mês <span>(R$)</span>
                            </S.LabelGraph>

                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                    data={transformMonthlySales(dataApi.monthly_sales)}
                                    margin={{
                                        top: 20,
                                        right: 30,
                                        left: 20,
                                        bottom: 5,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                                    <XAxis dataKey="name"
                                    />
                                    <YAxis />
                                    <Tooltip
                                        itemStyle={{ color: '#b3b1b1' }}
                                        cursor={{ fill: '#F4F4F5' }}
                                        formatter={(value, name) => [CurrencyFormat(Number(value)), name]}
                                    />
                                    <Bar
                                        dataKey="vendas"
                                        name={'Vendas'}
                                        radius={[8, 8, 0, 0]}
                                        fill='#FF0000'
                                        label={{
                                            position: 'top',
                                            formatter: (value: number) => CurrencyFormat(value)
                                        }}
                                    >
                                        {transformMonthlySales(dataApi.monthly_sales).map((entry: any, index: number) => (
                                            <Cell key={`cell-${index}`} fill={entry.month === currentDate.getMonth() + 1 ? '#FF0000' : '#D4D4D8'} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </S.FrameGraph>
                    </S.DividerGraph>

                    <S.DividerGraph>
                        <S.FrameGraph isLarge={true}>
                            <S.LabelGraph>
                                Total do Saldo por mês <span>(R$)</span>
                            </S.LabelGraph>

                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart
                                    width={500}
                                    height={400}
                                    data={transformMonthlySales(dataApi.monthly_balance)}
                                    margin={{
                                        top: 10,
                                        right: 30,
                                        left: 0,
                                        bottom: 0,
                                    }}
                                >
                                    <defs>
                                        <linearGradient id="colorUv" x1="0" y1="1" x2="0" y2="0">
                                            <stop offset="5%" stopColor="#FF0000" stopOpacity={0.03} />
                                            <stop offset="95%" stopColor="#FF0000" stopOpacity={0.5} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip formatter={(value: number, name: string) => [CurrencyFormat(value), name]} />
                                    <Area
                                        type="monotone"
                                        dataKey="vendas"
                                        stroke="#FF0000"
                                        fill="url(#colorUv)"
                                        dot={<CustomDot />}
                                    />
                                    <Line type="monotone" dataKey="vendas" stroke="#ff7300" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </S.FrameGraph>

                        <S.FrameGraph isLarge={false}>
                            <S.LabelGraph>
                                Fluxo de Caixa <span>(R$)</span>
                            </S.LabelGraph>

                            <S.ContentLine>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart width={400} height={400}>
                                        <Pie
                                            data={transformCashFlowToPieData(dataApi.cash_flow)}
                                            isAnimationActive={true}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={renderCustomizedLabel}
                                            outerRadius={80}
                                            dataKey="value"
                                        >
                                            {transformCashFlowToPieData(dataApi.cash_flow).map((entry: any, index: any) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value: number, name: string) => [CurrencyFormat(value), name]} />
                                    </PieChart>
                                </ResponsiveContainer>

                                <S.LabelGraph>
                                    <S.ContentLine>
                                        <S.CorLegenda color="#FF0000" />
                                        Entrada
                                    </S.ContentLine>
                                    <S.ContentLine>
                                        <S.CorLegenda color="#E4E4E7" />
                                        Saída
                                    </S.ContentLine>
                                </S.LabelGraph>
                            </S.ContentLine>
                        </S.FrameGraph>
                    </S.DividerGraph>
                </S.Content>
            )}
        </S.Container>
    )
}